// FAQ/홈 설정 draft의 "치명적 오류면 배포 차단" 검증. firestore.rules는 list-of-map 항목 하나하나를
// 깊게 검증하지 못하므로(기존 isValidAnnouncementData()도 평문자열 리스트까지만 rules에서 검증하고
// findPriorityConflicts 같은 항목 단위 비즈니스 검증은 전부 클라이언트 전용이다 — 동일 선례를 따른다),
// 이 파일이 실질적인 검증 경계다. errors는 배포를 막고, warnings는 막지 않는다.

import { CMS_HOME_CARD_IDS, CMS_HOME_DISABLEABLE_IDS, FACILITY_CATEGORIES } from '../types/cms.js'

const HTTPS_URL = /^https:\/\/.+/
const CAMPUS_CENTER = { lat: 37.2099, lng: 126.9757 }
const MAX_LAT_DELTA = 0.05
const MAX_LNG_DELTA = 0.07

function isValidCampusCoordinate(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng)
    && Math.abs(lat - CAMPUS_CENTER.lat) <= MAX_LAT_DELTA && Math.abs(lng - CAMPUS_CENTER.lng) <= MAX_LNG_DELTA
}

function validateHttpsPhotoUrl(item, errors) {
  const url = String(item?.photoUrl ?? '').trim()
  if (url && (!HTTPS_URL.test(url) || url.length > 2048)) errors.photoUrl = '사진 URL은 https://로 시작하는 2048자 이하 주소여야 합니다.'
}

function validateIdDupOrderCoords(items, errors, warnings, { requireCoords = true } = {}) {
  const seenIds = new Set()
  items.forEach((item, index) => {
    const id = String(item?.id ?? '').trim()
    if (!id) { errors.push({ index, field: 'id', message: `항목 ${index + 1}: id가 비어 있습니다.` }); return }
    if (seenIds.has(id)) errors.push({ index, id, field: 'id', message: '중복된 id입니다.' })
    seenIds.add(id)
    if (requireCoords) {
      const lat = Number(item.lat)
      const lng = Number(item.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) errors.push({ index, id, field: 'lat/lng', message: '좌표가 없거나 숫자가 아닙니다.' })
      else if (!isValidCampusCoordinate(lat, lng)) errors.push({ index, id, field: 'lat/lng', message: `좌표가 캠퍼스 반경을 크게 벗어났습니다(${lat}, ${lng}).` })
    }
  })
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0
}

// ── FAQ ──────────────────────────────────────────────────────────────────

export function validateFaqItem(item) {
  const errors = {}
  if (!isNonEmptyString(item?.id)) errors.id = 'ID를 입력하세요.'
  if (!isNonEmptyString(item?.question)) errors.question = '질문을 입력하세요.'
  else if (item.question.trim().length > 300) errors.question = '질문은 300자 이하여야 합니다.'
  if (!isNonEmptyString(item?.answer)) errors.answer = '답변을 입력하세요.'
  else if (item.answer.trim().length > 4000) errors.answer = '답변은 4000자 이하여야 합니다.'
  if (!isNonEmptyString(item?.category)) errors.category = '카테고리를 입력하세요.'
  if (item?.keywords !== undefined && !Array.isArray(item.keywords)) errors.keywords = '검색 키워드는 목록이어야 합니다.'
  if (!Number.isInteger(Number(item?.order))) errors.order = '순서는 정수여야 합니다.'
  if (typeof item?.active !== 'boolean') errors.active = '활성 여부를 선택하세요.'
  if (item?.officialUrl && (!HTTPS_URL.test(item.officialUrl) || item.officialUrl.length > 2048)) errors.officialUrl = '공식 URL은 https://로 시작하는 2048자 이하 주소여야 합니다.'
  if (item?.confidence && !['high', 'medium'].includes(item.confidence)) errors.confidence = '신뢰도는 high 또는 medium이어야 합니다.'
  return { valid: Object.keys(errors).length === 0, errors }
}

export function validateFaqDraft(data) {
  const errors = []
  const warnings = []
  const items = Array.isArray(data?.items) ? data.items : null
  if (!items) {
    errors.push({ field: 'items', message: 'FAQ 목록이 없습니다.' })
    return { valid: false, errors, warnings }
  }
  if (items.length === 0) warnings.push({ field: 'items', message: 'FAQ 항목이 하나도 없습니다.' })
  if (items.length > 1000) errors.push({ field: 'items', message: 'FAQ 항목은 1000개를 넘을 수 없습니다.' })

  const seenIds = new Set()
  items.forEach((item, index) => {
    const result = validateFaqItem(item)
    if (!result.valid) {
      Object.entries(result.errors).forEach(([field, message]) => errors.push({ index, id: item?.id, field, message }))
    }
    if (isNonEmptyString(item?.id)) {
      if (seenIds.has(item.id)) errors.push({ index, id: item.id, field: 'id', message: '중복된 ID입니다.' })
      seenIds.add(item.id)
    }
    if (item?.active !== false && !(item?.keywords?.length > 0)) warnings.push({ index, id: item?.id, field: 'keywords', message: '검색 키워드가 없으면 챗봇이 이 항목을 찾기 어렵습니다.' })
  })
  return { valid: errors.length === 0, errors, warnings }
}

// ── 홈 설정 ──────────────────────────────────────────────────────────────

function validateCardGroup(list, allowedIds, groupLabel, errors) {
  if (!Array.isArray(list)) { errors.push({ field: groupLabel, message: `${groupLabel} 설정이 목록이 아닙니다.` }); return }
  const ids = list.map(entry => entry?.id)
  const missing = allowedIds.filter(id => !ids.includes(id))
  const unknown = ids.filter(id => !allowedIds.includes(id))
  if (missing.length > 0) errors.push({ field: groupLabel, message: `${groupLabel}에 ${missing.join(', ')} 항목이 빠졌습니다.` })
  if (unknown.length > 0) errors.push({ field: groupLabel, message: `${groupLabel}에 알 수 없는 항목이 있습니다: ${unknown.join(', ')}` })
  list.forEach((entry) => {
    if (typeof entry?.visible !== 'boolean') errors.push({ field: groupLabel, id: entry?.id, message: 'visible 값이 boolean이 아닙니다.' })
    if (!Number.isInteger(Number(entry?.order))) errors.push({ field: groupLabel, id: entry?.id, message: 'order 값이 정수가 아닙니다.' })
  })
}

export function validateHomeConfigDraft(data) {
  const errors = []
  const warnings = []
  if (!data || typeof data !== 'object') { errors.push({ field: 'data', message: '설정 데이터가 없습니다.' }); return { valid: false, errors, warnings } }

  if (!['auto', 'maintenance'].includes(data.hero?.mode)) errors.push({ field: 'hero.mode', message: '히어로 모드는 auto 또는 maintenance여야 합니다.' })

  if (typeof data.maintenanceMessage?.enabled !== 'boolean') errors.push({ field: 'maintenanceMessage.enabled', message: '점검 메시지 노출 여부를 선택하세요.' })
  if (data.maintenanceMessage?.enabled && !isNonEmptyString(data.maintenanceMessage?.message)) errors.push({ field: 'maintenanceMessage.message', message: '점검 메시지 노출이 켜져 있으면 메시지 내용이 필요합니다.' })

  validateCardGroup(data.cards?.todayQuickChips, CMS_HOME_CARD_IDS.todayQuickChips, 'cards.todayQuickChips', errors)
  validateCardGroup(data.cards?.classicUtilityRow, CMS_HOME_CARD_IDS.classicUtilityRow, 'cards.classicUtilityRow', errors)
  if (typeof data.cards?.classicMealMapRow?.visible !== 'boolean') errors.push({ field: 'cards.classicMealMapRow', message: 'visible 값이 boolean이 아닙니다.' })
  if (typeof data.cards?.classicSugiInstantCard?.visible !== 'boolean') errors.push({ field: 'cards.classicSugiInstantCard', message: 'visible 값이 boolean이 아닙니다.' })

  if (!Array.isArray(data.disabledFeatures)) errors.push({ field: 'disabledFeatures', message: 'disabledFeatures는 목록이어야 합니다.' })
  else {
    const unknown = data.disabledFeatures.filter(id => !CMS_HOME_DISABLEABLE_IDS.includes(id) && !['classicMealMapRow', 'classicSugiInstantCard'].includes(id))
    if (unknown.length > 0) errors.push({ field: 'disabledFeatures', message: `알 수 없는 기능 id: ${unknown.join(', ')}` })
  }

  return { valid: errors.length === 0, errors, warnings }
}

// ── 건물 ─────────────────────────────────────────────────────────────────

export function validateBuildingItem(item) {
  const errors = {}
  if (!isNonEmptyString(item?.id)) errors.id = 'ID를 입력하세요.'
  if (!isNonEmptyString(item?.name)) errors.name = '이름을 입력하세요.'
  if (!isNonEmptyString(item?.category)) errors.category = '카테고리를 입력하세요.'
  const lat = Number(item?.lat)
  const lng = Number(item?.lng)
  if (!isValidCampusCoordinate(lat, lng)) errors.lat = '좌표가 없거나 캠퍼스 반경을 벗어났습니다.'
  const hasEntranceLat = item?.entranceLat !== '' && item?.entranceLat != null
  const hasEntranceLng = item?.entranceLng !== '' && item?.entranceLng != null
  if (hasEntranceLat || hasEntranceLng) {
    const entranceLat = Number(item.entranceLat)
    const entranceLng = Number(item.entranceLng)
    if (!isValidCampusCoordinate(entranceLat, entranceLng)) errors.entranceLat = '입구 좌표가 캠퍼스 반경을 벗어났습니다.'
  }
  if (item?.entranceStatus && !['pending', 'checked', 'fixed'].includes(item.entranceStatus)) errors.entranceStatus = '입구 상태는 pending/checked/fixed 중 하나여야 합니다.'
  validateHttpsPhotoUrl(item, errors)
  return { valid: Object.keys(errors).length === 0, errors }
}

export function validateBuildingsDraft(data) {
  const errors = []
  const warnings = []
  const items = Array.isArray(data?.items) ? data.items : null
  if (!items) { errors.push({ field: 'items', message: '건물 목록이 없습니다.' }); return { valid: false, errors, warnings } }
  if (items.length === 0) warnings.push({ field: 'items', message: '건물이 하나도 없습니다.' })

  items.forEach((item, index) => {
    const result = validateBuildingItem(item)
    if (!result.valid) Object.entries(result.errors).forEach(([field, message]) => errors.push({ index, id: item?.id, field, message }))
  })
  validateIdDupOrderCoords(items, errors, warnings, { requireCoords: false })
  return { valid: errors.length === 0, errors, warnings }
}

// ── 시설 ─────────────────────────────────────────────────────────────────

export function validateFacilityItem(item) {
  const errors = {}
  if (!isNonEmptyString(item?.id)) errors.id = 'ID를 입력하세요.'
  if (!isNonEmptyString(item?.name)) errors.name = '이름을 입력하세요.'
  if (!FACILITY_CATEGORIES.includes(item?.category)) errors.category = `카테고리는 ${FACILITY_CATEGORIES.join('/')} 중 하나여야 합니다.`
  const lat = Number(item?.lat)
  const lng = Number(item?.lng)
  if (!isValidCampusCoordinate(lat, lng)) errors.lat = '좌표가 없거나 캠퍼스 반경을 벗어났습니다.'
  validateHttpsPhotoUrl(item, errors)
  return { valid: Object.keys(errors).length === 0, errors }
}

export function validateFacilitiesDraft(data) {
  const errors = []
  const warnings = []
  const items = Array.isArray(data?.items) ? data.items : null
  if (!items) { errors.push({ field: 'items', message: '시설 목록이 없습니다.' }); return { valid: false, errors, warnings } }
  if (items.length === 0) warnings.push({ field: 'items', message: '시설이 하나도 없습니다.' })

  items.forEach((item, index) => {
    const result = validateFacilityItem(item)
    if (!result.valid) Object.entries(result.errors).forEach(([field, message]) => errors.push({ index, id: item?.id, field, message }))
  })
  validateIdDupOrderCoords(items, errors, warnings, { requireCoords: false })
  return { valid: errors.length === 0, errors, warnings }
}

// ── 학식 override ────────────────────────────────────────────────────────

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function validateCafeteriaOverrideItem(item) {
  const errors = {}
  if (!isNonEmptyString(item?.id)) errors.id = 'ID를 입력하세요.'
  if (!['lecture', 'staff'].includes(item?.restaurantId)) errors.restaurantId = '운영 중인 식당을 선택하세요.'
  if (!DATE_RE.test(String(item?.date ?? ''))) errors.date = '날짜는 YYYY-MM-DD 형식이어야 합니다.'
  for (const meal of ['breakfast', 'lunch', 'dinner']) {
    if (item?.[meal] !== undefined && !Array.isArray(item[meal])) errors[meal] = `${meal}은 목록이어야 합니다.`
  }
  if (typeof item?.closed !== 'boolean') errors.closed = '운영 여부(closed)를 boolean으로 지정하세요.'
  if (typeof item?.active !== 'boolean') errors.active = '활성 여부를 선택하세요.'
  return { valid: Object.keys(errors).length === 0, errors }
}

export function validateCafeteriaOverridesDraft(data) {
  const errors = []
  const warnings = []
  const items = Array.isArray(data?.items) ? data.items : null
  if (!items) { errors.push({ field: 'items', message: 'override 목록이 없습니다.' }); return { valid: false, errors, warnings } }
  const seenKeys = new Set()
  items.forEach((item, index) => {
    const result = validateCafeteriaOverrideItem(item)
    if (!result.valid) Object.entries(result.errors).forEach(([field, message]) => errors.push({ index, id: item?.id, field, message }))
    const key = `${item?.restaurantId}__${item?.date}`
    if (seenKeys.has(key)) errors.push({ index, id: item?.id, field: 'restaurantId/date', message: '같은 식당·날짜에 override가 중복됩니다.' })
    seenKeys.add(key)
  })
  if (data?.copy !== undefined) {
    if (!data.copy || typeof data.copy !== 'object' || Array.isArray(data.copy)) errors.push({ field: 'copy', message: '학식 안내 문구는 객체여야 합니다.' })
    else for (const field of ['mealEmptyTitle', 'mealEmptyDescription', 'weeklyEmptyTitle', 'weeklyEmptyDescription', 'dataErrorMessage']) {
      if (data.copy[field] !== undefined && typeof data.copy[field] !== 'string') errors.push({ field: `copy.${field}`, message: '안내 문구는 문자열이어야 합니다.' })
      if (typeof data.copy[field] === 'string' && data.copy[field].length > 200) errors.push({ field: `copy.${field}`, message: '안내 문구는 200자 이내여야 합니다.' })
    }
  }
  return { valid: errors.length === 0, errors, warnings }
}

// ── 공지 override ────────────────────────────────────────────────────────

export function validateNoticeOverrideItem(item) {
  const errors = {}
  if (!isNonEmptyString(item?.id)) errors.id = 'ID를 입력하세요.'
  const isAuthored = !isNonEmptyString(item?.sourceNoticeId)
  if (isAuthored && !isNonEmptyString(item?.title)) errors.title = '직접 작성한 공지는 제목이 필요합니다.'
  if (isAuthored && !isNonEmptyString(item?.category)) errors.category = '직접 작성한 공지는 카테고리가 필요합니다.'
  if (isAuthored && !DATE_RE.test(String(item?.date ?? ''))) errors.date = '게시일은 YYYY-MM-DD 형식이어야 합니다.'
  if (item?.url && !HTTPS_URL.test(item.url)) errors.url = '원문 URL은 https://로 시작해야 합니다.'
  if (typeof item?.important !== 'boolean') errors.important = '중요 여부를 선택하세요.'
  if (typeof item?.visible !== 'boolean') errors.visible = '앱 노출 여부를 선택하세요.'
  if (typeof item?.active !== 'boolean') errors.active = '활성 여부를 선택하세요.'
  return { valid: Object.keys(errors).length === 0, errors }
}

export function validateNoticeOverridesDraft(data) {
  const errors = []
  const warnings = []
  const items = Array.isArray(data?.items) ? data.items : null
  if (!items) { errors.push({ field: 'items', message: 'override 목록이 없습니다.' }); return { valid: false, errors, warnings } }
  const seenIds = new Set()
  const seenSourceIds = new Set()
  items.forEach((item, index) => {
    const result = validateNoticeOverrideItem(item)
    if (!result.valid) Object.entries(result.errors).forEach(([field, message]) => errors.push({ index, id: item?.id, field, message }))
    if (isNonEmptyString(item?.id)) {
      if (seenIds.has(item.id)) errors.push({ index, id: item.id, field: 'id', message: '중복된 id입니다.' })
      seenIds.add(item.id)
    }
    if (isNonEmptyString(item?.sourceNoticeId)) {
      if (seenSourceIds.has(item.sourceNoticeId)) errors.push({ index, id: item?.id, field: 'sourceNoticeId', message: '같은 원본 공지에 override가 중복됩니다.' })
      seenSourceIds.add(item.sourceNoticeId)
    }
  })
  return { valid: errors.length === 0, errors, warnings }
}

// ── 기능 설정 ────────────────────────────────────────────────────────────

const VERSION_RE = /^\d+(\.\d+){0,3}$/

export function validateFeatureSettingsDraft(data) {
  const errors = []
  const warnings = []
  if (!data || typeof data !== 'object') { errors.push({ field: 'data', message: '설정 데이터가 없습니다.' }); return { valid: false, errors, warnings } }

  if (typeof data.maintenanceMode?.enabled !== 'boolean') errors.push({ field: 'maintenanceMode.enabled', message: '점검 모드 여부를 선택하세요.' })
  if (data.maintenanceMode?.enabled && !isNonEmptyString(data.maintenanceMode?.message)) errors.push({ field: 'maintenanceMode.message', message: '점검 모드가 켜져 있으면 안내 메시지가 필요합니다.' })

  if (typeof data.operationalMessage?.enabled !== 'boolean') errors.push({ field: 'operationalMessage.enabled', message: '운영 메시지 노출 여부를 선택하세요.' })
  if (data.operationalMessage?.enabled && !isNonEmptyString(data.operationalMessage?.message)) errors.push({ field: 'operationalMessage.message', message: '운영 메시지 노출이 켜져 있으면 메시지 내용이 필요합니다.' })

  if (!data.featureToggles || typeof data.featureToggles !== 'object' || Array.isArray(data.featureToggles)) {
    errors.push({ field: 'featureToggles', message: 'featureToggles는 객체여야 합니다.' })
  } else {
    Object.entries(data.featureToggles).forEach(([key, value]) => {
      if (typeof value !== 'boolean') errors.push({ field: 'featureToggles', message: `${key}의 값은 boolean이어야 합니다.` })
    })
  }

  for (const platform of ['ios', 'android']) {
    const version = data.minVersion?.[platform]
    if (version && !VERSION_RE.test(String(version))) errors.push({ field: `minVersion.${platform}`, message: '버전 형식은 1~4개의 숫자를 점(.)으로 구분해야 합니다(예: 1.2.0).' })
    if (typeof data.forceUpdate?.[platform] !== 'boolean') errors.push({ field: `forceUpdate.${platform}`, message: 'boolean이어야 합니다.' })
    if (data.forceUpdate?.[platform] && !isNonEmptyString(version)) errors.push({ field: `minVersion.${platform}`, message: '강제 업데이트를 켜려면 최소 버전이 필요합니다.' })
  }
  if ((data.forceUpdate?.ios || data.forceUpdate?.android) && !isNonEmptyString(data.forceUpdate?.message)) {
    errors.push({ field: 'forceUpdate.message', message: '강제 업데이트가 켜져 있으면 안내 메시지가 필요합니다.' })
  }

  return { valid: errors.length === 0, errors, warnings }
}

export function validateCmsDraft(contentType, data) {
  if (contentType === 'faq') return validateFaqDraft(data)
  if (contentType === 'homeConfig') return validateHomeConfigDraft(data)
  if (contentType === 'buildings') return validateBuildingsDraft(data)
  if (contentType === 'facilities') return validateFacilitiesDraft(data)
  if (contentType === 'cafeteriaOverrides') return validateCafeteriaOverridesDraft(data)
  if (contentType === 'noticeOverrides') return validateNoticeOverridesDraft(data)
  if (contentType === 'featureSettings') return validateFeatureSettingsDraft(data)
  throw new Error(`unknown cms contentType: ${contentType}`)
}
